import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { StringNumberBooleanMap } from '@fcom/core-api';
import { RootPaths } from '@fcom/core';
import { toQueryParamString } from '@fcom/common/utils/booking-common.utils';
import { Step } from '@fcom/common';
import { LanguageService } from '@fcom/ui-translate';

import { VoluntaryChangeFeatureState } from '../interfaces/store.interface';
import { VOLUNTARY_CHANGE_STEPS, VOLUNTARY_CHANGE_STEPS_ARRAY } from '../voluntary-change-config';
import { setStep } from '../store/actions/state.actions';

export interface StepChangeSignature {
  onSuccessPost?: (step: Step) => void;
  skipRouteChange?: boolean;
}

export interface SetStepSignature extends StepChangeSignature {
  step: Step;
}

export const getStepLink = (lang: string, step: Step, params?: StringNumberBooleanMap): string =>
  `/${[lang, RootPaths.VOLUNTARY_CHANGE_ROOT, step.path].filter(Boolean).join('/')}${toQueryParamString(params)}`;

@Injectable()
export class VoluntaryChangeService {
  currentStepIndex = 0;

  constructor(
    private store$: Store<VoluntaryChangeFeatureState>,
    private router: Router,
    private languageService: LanguageService
  ) {}

  goToViewPNR(): void {
    this.router.navigate([this.languageService.langValue, 'manage', 'view']);
  }

  private goToStep({ step, skipRouteChange }: SetStepSignature): void {
    this.store$.dispatch(setStep({ step, skipRouteChange }));
    this.currentStepIndex = VOLUNTARY_CHANGE_STEPS_ARRAY.findIndex((s) => s.id === step.id);
  }

  isCurrentStep(step: Step): boolean {
    return this.currentStepIndex === VOLUNTARY_CHANGE_STEPS_ARRAY.findIndex((s) => s.id === step.id);
  }

  stepFrom(url: string): void {
    this.goToStep({ step: this.mapStep(url) });
  }

  mapStep(url: string): Step {
    return VOLUNTARY_CHANGE_STEPS_ARRAY.find((step) => url.includes(step.path)) ?? VOLUNTARY_CHANGE_STEPS.SELECT_BOUNDS;
  }

  setStep({ step, skipRouteChange }: SetStepSignature): void {
    this.goToStep({ step, skipRouteChange });
  }

  prevStep(): void {
    const nextStep = this.currentStepIndex - 1;
    if (
      nextStep >= 0 &&
      this.currentStepIndex !== VOLUNTARY_CHANGE_STEPS_ARRAY.indexOf(VOLUNTARY_CHANGE_STEPS.PURCHASE_SUCCESS)
    ) {
      this.setStep({
        step: VOLUNTARY_CHANGE_STEPS_ARRAY[nextStep],
      });
    }
  }

  nextStep(): void {
    const nextStep = this.currentStepIndex + 1;

    if (nextStep < VOLUNTARY_CHANGE_STEPS_ARRAY.length) {
      this.setStep({
        step: VOLUNTARY_CHANGE_STEPS_ARRAY[nextStep],
      });
    }
  }

  isFirstStep(): Observable<boolean> {
    return of(this.currentStepIndex === 0);
  }
}
